import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';

import { IconType } from 'app/shared/icon';
import { Snackbar } from 'app/shared/snackbar/snackbar';

interface SnackbarOverlayData {
    title: string;
    content?: string;
    icon?: IconType;
    iconFill?: string;
    duration: number;
    id?: string;
}

@Injectable({ providedIn: 'root' })
export class StackedNotificationService {
    private overlay = inject(Overlay);
    private overlayRefs: { ref: OverlayRef; height: number; key: string }[] = [];
    private readonly spacing = 12;
    private activeComponents = new Map<string, any>();

    show({ title, content, icon, iconFill, duration, id }: SnackbarOverlayData): void {
        // Create a unique key for this specific error content
        // Cannot use only id, as some errors might have the same id but different content, e.g. missing permissions
        const uniqueKey = `${id || 'no-id'}-${title}-${content || ''}`;

        if (this.activeComponents.has(uniqueKey)) {
            const activeInstance = this.activeComponents.get(uniqueKey);
            activeInstance.resetTimer();
            return;
        }

        const overlayRef = this.overlay.create({
            positionStrategy: this.getPositionStrategy(),
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            hasBackdrop: false,
        });

        const portal = new ComponentPortal(Snackbar);
        const componentRef = overlayRef.attach(portal);
        const instance = componentRef.instance;

        Object.assign(instance, { title, content, icon, iconFill, duration });

        this.activeComponents.set(uniqueKey, instance);
        this.overlayRefs.push({ ref: overlayRef, height: 72, key: uniqueKey });

        this.repositionSnackbars();

        setTimeout(() => {
            const element = overlayRef.overlayElement.firstElementChild as HTMLElement;
            if (element) {
                const actualHeight = element.offsetHeight;
                const entry = this.overlayRefs.find((r) => r.ref === overlayRef);
                if (entry && entry.height !== actualHeight) {
                    entry.height = actualHeight;
                    this.repositionSnackbars();
                }
            }
        });

        // No error handler needed: animationDone is a component @Output EventEmitter,
        // not a network call, and does not error.
        componentRef.instance.animationDone.subscribe((phase) => {
            if (phase === 'leave') {
                this.activeComponents.delete(uniqueKey);
                overlayRef.dispose();
                this.overlayRefs = this.overlayRefs.filter((item) => item.ref !== overlayRef);
                this.repositionSnackbars();
            }
        });
    }

    private repositionSnackbars(): void {
        let offset = 40;
        for (const item of this.overlayRefs) {
            const positionStrategy = this.overlay.position().global().top(`${offset}px`).centerHorizontally();
            item.ref.updatePositionStrategy(positionStrategy);
            offset += item.height + this.spacing;
        }
    }

    private getPositionStrategy() {
        const currentTotalHeight = this.overlayRefs.reduce((acc, curr) => acc + curr.height + this.spacing, 0);
        return this.overlay
            .position()
            .global()
            .top(`${40 + currentTotalHeight}px`)
            .centerHorizontally();
    }
}
